// @ts-nocheck
import React from 'react';

import * as styles from './styles';
import classNames from 'classnames';
import { debounce } from 'lodash';

import { EditIcon } from '../icons';

export interface EditableLabelProps {
  labelText?: string;
  className?: string;
  labelClassName?: string;
  iconClassName?: string;
  placeholder?: string;
  name?: string;
  charactersLimit?: number;
  debounceDelay?: number;
  showEditIcon?: boolean;
  onFocus?: () => void;
  onChange?: (newText: string) => void;
  onBlur?: (newText: string) => void;
}

export interface EditableLabelState {
  editing: boolean;
  labelText: string;
  startOffset: number;
}

/**
 * @deprecated This component is deprecated and will be removed in the future.
 * @description Now use '@cobliteam/ui/components/*' components.
 */
export class EditableLabel extends React.Component<EditableLabelProps, EditableLabelState> {
  private labelRef = React.createRef<HTMLSpanElement>();

  private selection = window.getSelection?.();

  private range = document.createRange();

  private debouncedOnChange = this.props.debounceDelay ? debounce((newText: string) => this.onChange(newText), this.props.debounceDelay) : undefined;

  private onChange = (newText?: string) => {
    this.debouncedOnChange?.cancel();
    if (this.props.labelText !== newText) {
      this.props.onChange?.(newText || '');
    }
  };

  constructor(props: EditableLabelProps) {
    super(props);

    this.state = {
      editing: false,
      labelText: this.props.labelText || '',
      startOffset: 0,
    };
  }

  public componentDidMount() {
    const label = this.labelRef.current;

    if (label) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'characterData') {
            const { textContent } = mutation.target as HTMLElement;

            if (textContent) {
              this.debouncedOnChange?.(textContent);
            }
          }
        });
      });

      observer.observe(label, { characterData: true, subtree: true });
    }
  }

  public shouldComponentUpdate(nextProps: EditableLabelProps, nextState: EditableLabelState) {
    const hasEditingStateChanged = nextState.editing !== this.state.editing;
    const isLabelPropDifferentFromState = nextProps.labelText !== this.state.labelText && !nextState.editing;
    const hasClassNameChanged = nextProps.className !== this.props.className;
    return hasEditingStateChanged || isLabelPropDifferentFromState || hasClassNameChanged;
  }

  public componentDidUpdate(prevProps: EditableLabelProps) {
    if (this.props.labelText !== prevProps.labelText) {
      this.setState({ labelText: this.props.labelText || '' }, () => {
        const label = this.labelRef.current?.firstChild;
        const selection = window.getSelection?.();

        if (this.state.startOffset && label && selection && Number(label?.textContent?.length) >= this.state.startOffset) {
          this.range.selectNodeContents(label);
          this.range.setStart(label, this.state.startOffset);
          this.range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(this.range);
        }
      });
    }
  }

  private startEditing = (event: React.MouseEvent) => {
    event.stopPropagation();

    const label = this.labelRef.current;
    const newState = { editing: true };

    this.setState(newState, () => {
      if (label && this.selection) {
        label.spellcheck = false;
        label.focus();

        this.range.selectNodeContents(label);
        this.selection.removeAllRanges();
        this.selection.addRange(this.range);
      }
    });
  };

  private handleBlur = (event: React.FocusEvent) => {
    this.setState({ editing: false });
    this.debouncedOnChange?.cancel();
    this.handleChange(event);
    this.props.onBlur?.(event.currentTarget.textContent || '');
  };

  private handleKeyDown = (event: React.KeyboardEvent) => {
    const { textContent } = event.currentTarget;

    if (
      textContent &&
      textContent.length === this.props.charactersLimit &&
      event.keyCode !== 8 // Delete key
    ) {
      event.preventDefault();
    }

    if (event.keyCode === 13) {
      event.preventDefault();
      // @ts-ignore
      return this.props.onBlur ? this.handleBlur(event) : this.handleChange(event);
    }

    // Used to save the caret position, I din't find a better way to do it without using a setTimeout.
    setTimeout(() => {
      this.setState({
        startOffset: window.getSelection()?.getRangeAt(0).startOffset || 0,
      });
    }, 0);
  };

  private handleChange = (event: React.SyntheticEvent<Element>) => {
    const { labelText, placeholder } = this.props;
    const { textContent } = event.currentTarget;
    const newText = textContent ? textContent.trim() : '';
    const label = this.labelRef.current;

    // TS safety null check
    if (this.selection && label && label.parentElement) {
      this.selection.removeAllRanges();
      label.parentElement.scrollLeft = 0;
    }

    if (!placeholder && (!newText || newText.length === 0)) {
      event.currentTarget.textContent = labelText || '';
      return this.setState({ editing: false });
    }

    this.setState({ editing: false, labelText: newText }, () => {
      // This is a workaround for an issue with the onBlur and contentEditable
      setTimeout(() => this.onChange(newText), 100);
    });
  };

  public render() {
    const { className, labelClassName, iconClassName, placeholder, showEditIcon, onFocus } = this.props;
    const { editing, labelText } = this.state;
    const showIcon = !editing && (Boolean(labelText) || showEditIcon);

    return (
      <span className={classNames(styles.editableContainer, className)} onClick={this.startEditing}>
        <span className={styles.labelContainer}>
          <span
            className={classNames(styles.editableLabel, labelClassName)}
            contentEditable={editing}
            onFocus={onFocus}
            onBlur={this.handleBlur}
            onKeyDown={this.handleKeyDown}
            suppressContentEditableWarning
            ref={this.labelRef}
            placeholder={placeholder}
            itemID={this.props.name}
          >
            {labelText}
          </span>
        </span>
        {showIcon && <EditIcon className={classNames(styles.editIcon, iconClassName)} />}
      </span>
    );
  }
}
